import { z } from 'zod';

import { getTranslation } from './get-translation.util';

// The raw issue is a union of per-code shapes; reading the few fields the messages need off one
// flat type saves narrowing each variant by hand.
type IssueFields = {
  code?: string;
  expected?: string;
  input?: unknown;
  maximum?: bigint | number;
  minimum?: bigint | number;
  origin?: string;
};

const isEmptyInput = (input: unknown): boolean => input === null || input === undefined;

const isInvalidDate = (input: unknown): boolean =>
  input instanceof Date && Number.isNaN(input.getTime());

// `z.coerce.date()` turns an untouched date field into an Invalid Date instead of leaving it
// undefined, so an empty picker arrives here as a date issue rather than a missing value.
const invalidTypeMessage = ({ expected, input }: IssueFields): string => {
  if (isEmptyInput(input)) return getTranslation('REQUIRED');
  if (expected === 'date') return isInvalidDate(input) ? getTranslation('INVALID_DATE') : '';
  if (expected === 'number') return getTranslation('PLEASE_ENTER_NUMERIC_VALUE');

  return '';
};

const tooSmallMessage = ({ minimum, origin }: IssueFields): string =>
  Number(minimum) === 1 && (origin === 'array' || origin === 'string')
    ? getTranslation('REQUIRED')
    : '';

const tooBigMessage = ({ maximum, origin }: IssueFields): string => {
  const count = Number(maximum);

  if (origin === 'string') return getTranslation('CHARACTER_LIMIT_IS_COUNT', { count });
  if (origin === 'array') return getTranslation('ITEMS_MAXIMUM', { count });

  return '';
};

const MESSAGE_BY_ISSUE_CODE: Record<string, (issue: IssueFields) => string> = {
  invalid_type: invalidTypeMessage,
  invalid_value: ({ input }) => (isEmptyInput(input) ? getTranslation('REQUIRED') : ''),
  too_big: tooBigMessage,
  too_small: tooSmallMessage,
};

// Zod writes its fallbacks in schema vocabulary: an unselected event combobox read "Invalid input:
// expected object, received null" on the add-event form. Translate the generic issues into the
// wording the forms already use.
//
// Fields that carry their own message never reach here — a schema-level error outranks this map, so
// it only fills the gaps. Anything left unmapped returns undefined and keeps Zod's own text.
export const friendlyZodMessage = (issue: z.core.$ZodRawIssue): string | undefined =>
  MESSAGE_BY_ISSUE_CODE[issue.code ?? '']?.(issue as IssueFields) || undefined;
